import { useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router'
import {
	getBlogTagBySlug,
	getLocalizedField,
	getPublishedPosts,
	type SupportedLocale,
} from '#app/utils/blog.server.ts'
import { useLang, getHreflangLinks } from '#app/utils/i18n.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { BlogListing } from './+blog-components/blog-listing.tsx'
import { type Route } from './+types/blog.tag.$tagSlug.ts'

const PAGE_SIZE = 12

const translations = {
	postsWithTag: {
		de: (count: number, name: string) =>
			`${count} ${count === 1 ? 'Beitrag' : 'Beiträge'} mit Tag «${name}»`,
		fr: (count: number, name: string) =>
			`${count} ${count === 1 ? 'article' : 'articles'} avec le tag «${name}»`,
		en: (count: number, name: string) =>
			`${count} ${count === 1 ? 'post' : 'posts'} tagged "${name}"`,
	},
	tagPrefix: {
		de: 'Tag: ',
		fr: 'Tag : ',
		en: 'Tag: ',
	},
} as const

export async function loader({ params, request }: Route.LoaderArgs) {
	const tag = await getBlogTagBySlug(params.tagSlug)

	if (!tag) {
		throw new Response('Not found', { status: 404 })
	}

	const url = new URL(request.url)
	const page = Math.max(1, Number(url.searchParams.get('page')) || 1)

	const { posts, totalCount, totalPages, currentPage } =
		await getPublishedPosts({
			page,
			pageSize: PAGE_SIZE,
			tagSlug: params.tagSlug,
		})

	const locale = (params.lang || 'de') as SupportedLocale
	const tagName = getLocalizedField(tag, 'name', locale)
	const siteUrl = process.env.APP_URL || getDomainUrl(request)

	return {
		tag,
		tagName,
		tagSlug: tag.slug,
		posts,
		totalCount,
		totalPages,
		currentPage,
		siteUrl,
	}
}

export const meta: Route.MetaFunction = ({ data, params }) => {
	if (!data) {
		return [{ title: '404 | Blog | Open Sourced' }]
	}

	const tags: ReturnType<Route.MetaFunction> = [
		{ title: `Tag: ${data.tagName} | Blog | Open Sourced` },
		{
			name: 'description',
			content: `Tag: ${data.tagName}, Blog-Beiträge auf Open Sourced`,
		},
	]

	const pathname = `/${params.lang || 'de'}/blog/tag/${data.tagSlug}`
	const hreflangLinks = getHreflangLinks(pathname, data.siteUrl)
	tags.push(...hreflangLinks)

	return tags
}

export default function BlogTagPage() {
	const { posts, totalCount, totalPages, currentPage, tagName, tagSlug } =
		useLoaderData<typeof loader>()
	const lang = useLang()
	const { t } = useTranslation()

	return (
		<BlogListing
			title={`${translations.tagPrefix[lang]}${tagName}`}
			summary={translations.postsWithTag[lang](totalCount, tagName)}
			empty={{
				title: t('blog.emptyTag.title'),
				description: t('blog.emptyTag.description'),
			}}
			posts={posts}
			currentPage={currentPage}
			totalPages={totalPages}
			basePath={`/blog/tag/${tagSlug}`}
		/>
	)
}
